/**
 * Hierarchie-Regeln für die UI (Vertrag 0.5): Rollen nur unterhalb der eigenen höchsten Rolle verwalten,
 * nur Mitglieder mit niedrigerer höchster Rolle moderieren, nur eigene Rechte vergeben (außer Owner/ADMINISTRATOR).
 * Die API prüft dasselbe – die UI deaktiviert nur, was ohnehin abgelehnt würde.
 */
import { ALL_PERMISSIONS, Permissions, toBitfield, type MemberDto, type RoleDto } from '@lagerfeuer/shared';
import { useMemo } from 'react';
import { useAppStore } from '../../store/app';
import { highestRolePosition, serverPermissions } from '../../store/selectors';
import type { AppData } from '../../store/types';

export interface Actor {
  isOwner: boolean;
  isAdmin: boolean;
  /** Effektive Serverrechte. */
  perms: bigint;
  /** Position der eigenen höchsten Rolle (0 = nur @everyone). */
  top: number;
  meId: string;
  ownerId: string;
}

export function actorFor(s: AppData, serverId: string): Actor | null {
  const server = s.servers[serverId];
  if (!server || !s.me) return null;
  const roles = s.roles[serverId] ?? [];
  const isOwner = server.ownerId === s.me.id;
  const perms = serverPermissions(s, serverId);
  const ownRoles = roles.filter((r) => server.me.roleIds.includes(r.id) || r.isEveryone);
  const isAdmin = isOwner || ownRoles.some((r) => (toBitfield(r.permissions) & Permissions.ADMINISTRATOR) !== 0n);
  return { isOwner, isAdmin, perms, top: highestRolePosition(roles, server.me), meId: s.me.id, ownerId: server.ownerId };
}

/** Rechte-Bits, die der Akteur vergeben (oder entziehen) darf. */
export function grantable(a: Actor): bigint {
  return a.isOwner || a.isAdmin ? ALL_PERMISSIONS : a.perms;
}

function hasBit(perms: bigint, bit: bigint): boolean {
  return (perms & Permissions.ADMINISTRATOR) !== 0n || (perms & bit) === bit;
}

/** Darf die Rolle bearbeitet werden? (@everyone: nur Rechte, mit MANAGE_ROLES immer.) */
export function canEditRole(a: Actor, role: RoleDto): boolean {
  if (role.managed) return false;
  if (a.isOwner) return true;
  if (!hasBit(a.perms, Permissions.MANAGE_ROLES)) return false;
  if (role.isEveryone) return true;
  return role.position < a.top;
}

/** Darf die Rolle einem Mitglied gegeben/genommen werden? */
export function canAssignRole(a: Actor, role: RoleDto): boolean {
  if (role.isEveryone || role.managed) return false;
  if (a.isOwner) return true;
  if (!hasBit(a.perms, Permissions.MANAGE_ROLES) || role.position >= a.top) return false;
  // Wie Backend `assertGrantable`: keine Rolle vergeben, die Rechte enthält, die man selbst nicht hat.
  return (toBitfield(role.permissions) & ~grantable(a)) === 0n;
}

/** Darf das Mitglied moderiert werden (Kick/Bann/Timeout/Spitzname/Rollen)? */
export function canActOn(a: Actor, roles: RoleDto[], target: Pick<MemberDto, 'roleIds'> & { user: { id: string } }): boolean {
  if (target.user.id === a.ownerId) return false;
  if (a.isOwner) return target.user.id !== a.meId;
  if (target.user.id === a.meId) return false;
  return highestRolePosition(roles, target) < a.top;
}

export function hasPerm(a: Actor, bit: bigint): boolean {
  return a.isOwner || hasBit(a.perms, bit);
}

export function useActor(serverId: string): Actor | null {
  const server = useAppStore((s) => s.servers[serverId]);
  const roles = useAppStore((s) => s.roles[serverId]);
  const me = useAppStore((s) => s.me);
  return useMemo(() => actorFor(useAppStore.getState(), serverId), [server, roles, me, serverId]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Rollenfarbe (Int) als CSS-Farbe. */
export function roleColor(color: number | null): string | null {
  return color ? `#${color.toString(16).padStart(6, '0')}` : null;
}
